import { createEventBus } from "./events.js";
import { createStore } from "./state.js";
import { createUiShell } from "./ui/shell.js";
import { createServices } from "./services/index.js";
import { installCrsPickers } from "./ui/crs-picker.js";
import { loadTools } from "../tools/index.js";
import { createMap } from "./map.js"; // <-- IMPORTANT
import { createLandingOverlay } from "./ui/landing.js";

export function createApp({ rootEl, mapEl, leftPanelEl, rightPanelEl, bottomPanelEl, topbarEl, hudEl, auth = null }) {
  console.log("[HydroTools] createApp() running. mapEl:", mapEl);

  const events = createEventBus();
  const store = createStore(events);
  const ui = createUiShell({ rootEl, leftPanelEl, rightPanelEl, bottomPanelEl, topbarEl, hudEl, mapEl, events, store });

  // Create Leaflet map instance (NO STUB)
  const map = { el: mapEl, instance: null };

  console.log("[HydroTools] calling createMap()...");
  map.instance = createMap({ mapEl, events });
  console.log("[HydroTools] map.instance =", map.instance);

  const services = createServices({ events, store, ui, map });

  // Blocks interaction with the map/tools until a project is explicitly activated -- see
  // landing.js. Created here (not inside a tool) since it needs to gate everything, not just
  // one panel.
  createLandingOverlay({ rootEl, events, context: services.context });

  const view = (() => {
    const host = mapEl?.parentElement || null;
    const topbarHost = topbarEl || null;
    const pane = document.createElement("div");
    pane.className = "potree-pane";
    pane.style.display = "none";
    pane.innerHTML = `
      <iframe class="potree-pane-frame" title="Potree Viewer" referrerpolicy="no-referrer"></iframe>
    `;
    const frame = pane.querySelector(".potree-pane-frame");
    let lastPotreeUrl = "";
    let currentMode = "map";
    if (host) host.appendChild(pane);

    const toggle = document.createElement("div");
    toggle.className = "view-toggle";
    toggle.innerHTML = `
      <button type="button" class="btn view-toggle-btn is-active" data-view-mode="map">Map</button>
      <button type="button" class="btn view-toggle-btn" data-view-mode="potree">Potree</button>
    `;
    if (topbarHost) topbarHost.appendChild(toggle);
    const mapBtn = toggle.querySelector('[data-view-mode="map"]');
    const potreeBtn = toggle.querySelector('[data-view-mode="potree"]');

    function updateToggleUi() {
      mapBtn?.classList.toggle("is-active", currentMode === "map");
      potreeBtn?.classList.toggle("is-active", currentMode === "potree");
      potreeBtn.disabled = !lastPotreeUrl;
      potreeBtn.title = lastPotreeUrl ? "Show Potree viewer" : "Open a Potree viewer first";
    }

    function showMap() {
      if (!host) return;
      currentMode = "map";
      host.dataset.mainView = "map";
      mapEl.style.display = "";
      if (hudEl) hudEl.style.display = "";
      pane.style.display = "none";
      updateToggleUi();
      try { map.instance?.invalidateSize?.(false); } catch {}
      setTimeout(() => {
        try { map.instance?.invalidateSize?.(false); } catch {}
      }, 120);
    }

    function showPotree(url) {
      if (!host) return;
      const nextUrl = String(url || lastPotreeUrl || "").trim();
      if (!nextUrl) return;
      lastPotreeUrl = nextUrl;
      currentMode = "potree";
      host.dataset.mainView = "potree";
      if (frame && frame.getAttribute("src") !== nextUrl) frame.setAttribute("src", nextUrl);
      mapEl.style.display = "none";
      if (hudEl) hudEl.style.display = "none";
      pane.style.display = "";
      updateToggleUi();
    }

    // Fixed 50/50 map|Potree split — CSS-only (no draggable divider in v1; see
    // .main[data-main-view="split"] rules in dashboard.css). Entry point is a tile's map
    // popup ("View in 3D"), not the Map/Potree toggle above, so there's no third toggle
    // button here — showMap()/showPotree() both already reset host.dataset.mainView, which
    // is what the split-mode CSS is gated on, so either one cleanly exits split.
    function showSplit(url) {
      if (!host) return;
      const nextUrl = String(url || lastPotreeUrl || "").trim();
      if (!nextUrl) return;
      lastPotreeUrl = nextUrl;
      currentMode = "split";
      host.dataset.mainView = "split";
      if (frame && frame.getAttribute("src") !== nextUrl) frame.setAttribute("src", nextUrl);
      mapEl.style.display = "";
      if (hudEl) hudEl.style.display = "";
      pane.style.display = "";
      updateToggleUi();
      try { map.instance?.invalidateSize?.(false); } catch {}
      setTimeout(() => {
        try { map.instance?.invalidateSize?.(false); } catch {}
      }, 120);
    }

    function getPotreeWindow() {
      try { return frame?.contentWindow || null; } catch { return null; }
    }

    // Lets callers (e.g. the tile-index mosaic flow) tell whether a scene is already open in
    // split view, to decide between starting a fresh session vs. adding to the live one —
    // mosaic sessions are meant to die whenever the user actually leaves split view.
    function getMode() {
      return currentMode;
    }

    mapBtn?.addEventListener("click", showMap);
    potreeBtn?.addEventListener("click", () => showPotree(lastPotreeUrl));
    updateToggleUi();
    return { showMap, showPotree, showSplit, getPotreeWindow, getMode };
  })();

  const appContext = {
    events,
    store,
    ui,
    map,
    view,
    services,
    auth,
    log: async (action, details = {}) => {
      try {
        // Every call site already threads the marina/project code through `details`
        // (as `marina` or `projectCode`) — lift it into its own column so the backend
        // can group activity by project without parsing JSONB.
        const projectCode = details?.marina || details?.projectCode || null;
        await fetch("/api/activity/log", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action, details, projectCode, userEmail: auth?.email ?? null, userSub: auth?.sub ?? null }),
        });
      } catch {}
    },
  };

  // One shared CRS picker everywhere: mounts every crsPickerField() as it appears (see crs-picker.js)
  installCrsPickers(appContext);
  const tools = loadTools(appContext);

  if (import.meta.env.DEV || location.search.includes("devperf")) {
    import("../dev/perf-overlay.js").then(({ mountPerfOverlay }) => {
      mountPerfOverlay({ events, tools });
    });
  }

  const runtime = Object.freeze({
    ...appContext,
    tools,
  });

  ui.bindRuntime?.(runtime);
  window.__hydrotools = runtime;
  return runtime;
}
